const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
let gisScriptPromise = null;
export function getViteGoogleClientId() {
    const value = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}
export function isViteDevMode() {
    return Boolean(import.meta.env.DEV);
}
export function canUseGoogleOAuthStub() {
    return isViteDevMode() && !getViteGoogleClientId();
}
function loadGoogleIdentityServices() {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Google Identity Services require a browser'));
    }
    if (window.google?.accounts?.id) {
        return Promise.resolve();
    }
    if (gisScriptPromise) {
        return gisScriptPromise;
    }
    gisScriptPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${GIS_SCRIPT_SRC}"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve(), { once: true });
            existing.addEventListener('error', () => reject(new Error('Failed to load Google Identity Services')), {
                once: true,
            });
            return;
        }
        const script = document.createElement('script');
        script.src = GIS_SCRIPT_SRC;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
        document.head.appendChild(script);
    });
    return gisScriptPromise;
}
export function createDevGoogleStubRequest() {
    const random = Math.random().toString(36).slice(2, 10);
    return {
        provider: 'GOOGLE',
        stubEmail: `google.dev.${random}@fetmode.local`,
        stubName: `Google Dev ${random}`,
    };
}
/**
 * Prompts Google One Tap / credential flow and resolves with an ID token.
 */
export async function requestGoogleIdToken() {
    const clientId = getViteGoogleClientId();
    if (!clientId) {
        throw new Error('VITE_GOOGLE_CLIENT_ID is not configured');
    }
    await loadGoogleIdentityServices();
    const googleId = window.google?.accounts?.id;
    if (!googleId) {
        throw new Error('Google Identity Services unavailable');
    }
    return new Promise((resolve, reject) => {
        let settled = false;
        googleId.initialize({
            client_id: clientId,
            callback: (response) => {
                if (settled) {
                    return;
                }
                settled = true;
                if (!response.credential) {
                    reject(new Error('Google did not return a credential'));
                    return;
                }
                resolve(response.credential);
            },
            auto_select: false,
            cancel_on_tap_outside: true,
        });
        googleId.prompt((notification) => {
            if (settled) {
                return;
            }
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                settled = true;
                reject(new Error('Google sign-in was cancelled or unavailable'));
            }
        });
    });
}
export async function buildGoogleOAuthLoginRequest() {
    if (getViteGoogleClientId()) {
        const idToken = await requestGoogleIdToken();
        return {
            provider: 'GOOGLE',
            idToken,
        };
    }
    if (canUseGoogleOAuthStub()) {
        return createDevGoogleStubRequest();
    }
    throw new Error('Google OAuth is not configured');
}
//# sourceMappingURL=google-oauth.js.map